import { DateTime } from 'luxon';
import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { PensionCalcData, useGetPensionCalcData } from '../../../api/pension-robot/useGetPensionCalcData';
import { PensionContext, PensionParameters } from '../../../contexts/pension-robot/PensionContext';
import { AgeInput } from '../../general/AgeInput';
import { YesNoFormGroup } from '../../general/form-inputs/YesNoFormGroup';
import { LoadingStateWrapper } from '../../general/LoadingStateWrapper';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { SavingButtons } from './DialogueDataSaver';
import { BackButton, NextButton } from './NavigationButtons';

export const AOW_SINGLE_TO_TOGETHER_FACTOR = 5 / 7;

export const PensionAOWSection: React.FC<{ isPartnerSection?: boolean }> = ({ isPartnerSection = false }) => {
    const { t } = useTranslation('pension');
    const {
        parameters: {
            pensionInfo,
            personalInfo,
        },
        actions,
    } = useContext(PensionContext);

    // Use current years data
    const AOWYear = DateTime.now().year;
    const pensionCalcDataQuery = useGetPensionCalcData(AOWYear);
    const pensionCalcData = pensionCalcDataQuery.data;
    return (
        <CocktailWrapperScreen
            page={3}
            title={
                isPartnerSection
                    ? t('pension-cocktail-fill-screen-aow-title-partner')
                    : t('pension-cocktail-fill-screen-aow-title')
            }
        >
            <p className="mb-4 text-gray-800">
                {isPartnerSection
                    ? t('pension-cocktail-fill-screen-aow-subtitle-partner')
                    : t('pension-cocktail-fill-screen-aow-subtitle')}
            </p>
            <LoadingStateWrapper 
                isLoading={pensionCalcDataQuery.isLoading}
                isError={pensionCalcDataQuery.isError}
                isIdle={pensionCalcDataQuery.isIdle}
                isFetching={pensionCalcDataQuery.isFetching}
            >
                {!isPartnerSection && (
                    <YesNoFormGroup
                        className="mb-4"
                        title={{
                            label: t('pension-cocktail-fill-screen-aow-abroad-btn-label'),
                            tooltip: { title: 'TODO', text: 'TODO' },
                        }}
                        value={
                            pensionInfo?.self?.isEmployedAbroad === null ||
                            pensionInfo?.self?.isEmployedAbroad === undefined
                                ? null
                                : !!pensionInfo?.self?.isEmployedAbroad
                                ? 'yes'
                                : 'no'
                        }
                        onChange={(value) => {
                            if (value === 'yes') {
                                actions.setParameters({ pensionInfo: { self: { isEmployedAbroad: true } } });
                            }
                            if (value === 'no') {
                                actions.setParameters({
                                    pensionInfo: {
                                        self: {
                                            isEmployedAbroad: false,
                                            aowGrossYearly: {
                                                single: pensionCalcData ? getAOWGrossYearly(pensionCalcData, false) : 0,
                                                together: pensionCalcData ? getAOWGrossYearly(pensionCalcData, true) : 0,
                                            },
                                        },
                                    },
                                });
                            }
                        }}
                    />
                )}
                {!!isPartnerSection && (
                    <YesNoFormGroup
                        className="mb-4"
                        title={{
                            label: t('pension-cocktail-fill-screen-aow-abroad-btn-label-partner'),
                            tooltip: { title: 'TODO', text: 'TODO' },
                        }}
                        value={
                            pensionInfo?.partner?.isEmployedAbroad === null ||
                            pensionInfo?.partner?.isEmployedAbroad === undefined
                                ? null
                                : !!pensionInfo?.partner?.isEmployedAbroad
                                ? 'yes'
                                : 'no'
                        }
                        onChange={(value) => {
                            if (value === 'yes') {
                                actions.setParameters({ pensionInfo: { partner: { isEmployedAbroad: true } } });
                            }
                            if (value === 'no') {
                                actions.setParameters({
                                    pensionInfo: {
                                        partner: {
                                            isEmployedAbroad: false,
                                            aowGrossYearly: pensionCalcData ? getAOWGrossYearly(pensionCalcData, true) : 0,
                                        },
                                    },
                                });
                            }
                        }}
                    />
                )}
            </LoadingStateWrapper>

            {!isPartnerSection && pensionInfo?.self?.isEmployedAbroad && (
                <AgeInput
                    suffix={t('general-year')}
                    title={{
                        label: t('pension-cocktail-fill-screen-aow-abroad-label')
                    }}
                    value={pensionInfo?.self?.yearsEmployedAbroad}
                    onChange={(value = '') => {
                        actions.setParameters({
                            pensionInfo: {
                                self: {
                                    aowGrossYearly: {
                                        single: pensionCalcData ? getAOWGrossYearly(pensionCalcData, false, +value) : 0,
                                        together: pensionCalcData ? getAOWGrossYearly(pensionCalcData, true, +value) : 0,
                                    },
                                    yearsEmployedAbroad: +value
                                },
                            },
                        });
                    }}
                    maxAge={100}
                    className="mb-8"
                />
            )}
            {!!isPartnerSection && pensionInfo?.partner?.isEmployedAbroad && (
                <AgeInput
                    suffix={t('general-year')}
                    title={{
                        label: t('pension-cocktail-fill-screen-aow-abroad-label-partner')
                    }}
                    value={pensionInfo?.partner?.yearsEmployedAbroad}
                    onChange={(value = '') => {
                        actions.setParameters({
                            pensionInfo: {
                                partner: {
                                    aowGrossYearly: pensionCalcData ? getAOWGrossYearly(pensionCalcData, true, +value) : 0,
                                    yearsEmployedAbroad: +value
                                },
                            },
                        });
                    }}
                    maxAge={100}
                    className="mb-8"
                />
            )}

            <div className="flex flex-row flex-wrap gap-4 items-start my-8">
                <BackButton />
                <NextButton
                    nextPage={
                        isPartnerSection || !personalInfo?.isCalculatedTogether
                            ? 'pension-gross'
                            : 'pension-aow-partner'
                    }
                    isDisabled={!isValid(pensionInfo, isPartnerSection)}
                />
            </div>
            <SavingButtons/>
        </CocktailWrapperScreen>
    );
};

function isValid(parameters: PensionParameters['pensionInfo'], isPartnerSection: boolean): boolean {
    if (isPartnerSection) {
        return parameters?.partner?.isEmployedAbroad === false || (!!parameters?.partner?.aowGrossYearly && !!parameters?.partner?.yearsEmployedAbroad);
    } else {
        return parameters?.self?.isEmployedAbroad === false || (!!parameters?.self?.aowGrossYearly && !!parameters?.self?.yearsEmployedAbroad);
    }
}

const getAOWGrossYearly = (pensionCalcData: PensionCalcData, livingTogether: boolean, yearsEmployedAbroad: number = 0) => {
    const baseAOW = livingTogether ? pensionCalcData.livingTogetherAOWAmount : pensionCalcData.singleAOWAmount;
    return (baseAOW ?? 0) * 12 * (1 - 0.02 * yearsEmployedAbroad) * 100;
} 
